import { DocumentType } from '@/models/document'
import type { CoverageType } from '@/models/insuranceCoverage'
import {
  InsuranceIndicationStatus,
  InsuranceQuoteStatus,
  type PremiumRange,
  type Vendor,
} from '@/models/insuranceTypes'

const QUOTE_VALIDITY_DAYS = 30

/**
 * Blank state for every carrier-sourced row. Each row renders it rather than being
 * dropped, so two cards side by side hold the same rows in the same order — and it
 * doubles as the value the backend already sends for an undisclosed claims capability.
 */
export const NO_INFORMATION = 'No information'

/** Tracks the phone's width, leaving the next card peeking, between 18rem and 26rem. */
export const QUOTE_CARD_WIDTH = 'w-[clamp(18rem,calc(100vw-3rem),26rem)]'

/** Internal-only distinction — never surfaced as "indication" in UI copy; shown as "Carrier estimate". */
export enum PricingKind {
  INDICATION = 'indication',
  QUOTED = 'quoted',
}

export enum IndicationStatus {
  WILL_REQUEST_INFO = 'will_request_info',
  AWAITING_LOSS_RUNS = 'awaiting_loss_runs',
  /** The carrier turned down the loss runs on file as too old. */
  AWAITING_RECENT_LOSS_RUNS = 'awaiting_recent_loss_runs',
  AWAITING_IFTA = 'awaiting_ifta',
  AWAITING_MVR = 'awaiting_mvr',
  HAS_QUESTIONS = 'has_questions',
  NEEDS_ATTESTATION = 'needs_attestation',
  REVIEWING_SUBMITTED_INFO = 'reviewing_submitted_info',
}

/**
 * Whether the carrier is admitted in the customer's state — i.e. whether the state
 * guaranty fund backstops claims if the carrier fails, and whether surplus lines taxes
 * apply on top of the premium.
 */
export enum InsuranceType {
  ADMITTED = 'admitted',
  NON_ADMITTED = 'non_admitted',
}

/** Financial-strength rating, shown A+ through F on every card regardless of stage. */
export type CarrierRating =
  | 'A++'
  | 'A+'
  | 'A'
  | 'A-'
  | 'B++'
  | 'B+'
  | 'B'
  | 'B-'
  | 'C++'
  | 'C+'
  | 'C'
  | 'C-'
  | 'D'
  | 'F'
  /** Published by brokers and RRGs whose paper varies by risk, instead of one grade. */
  | 'A- or better'

export enum CoiSpeed {
  UNDER_24_HOURS = 'under_24_hours',
  ONE_DAY_OR_MORE = 'one_day_or_more',
}

/**
 * How a multi-coverage QUOTED card's "Add to cart" is presented. Unset means the
 * coverages form a bundle — a single "Add to cart" applies to all of them, and the
 * customer must accept every coverage listed to get this price.
 */
export enum ItemizedCheckoutStyle {
  /** Design 1: full-width primary "Add to cart" directly below each coverage; no bundle CTA. */
  PER_COVERAGE = 'per_coverage',
  /** Design 2: small secondary "Add to cart" inline next to each coverage's Deductible, plus a primary bundle CTA at the bottom. */
  CAN_PURCHASE_INDIVIDUAL = 'can_purchase_individual',
}

/** Where a card is in the application lifecycle — most of the existing pricing detail only applies once PRICED. */
export enum QuoteCardStage {
  PREPARING = 'preparing',
  APPLICATION_SENT = 'application_sent',
  DECLINED = 'declined',
  /** Carrier could not produce a price due to a technical failure — distinct from DECLINED. */
  ERROR = 'error',
  /** Carrier doesn't write this risk (state, commodity, fleet size, etc.) — distinct from DECLINED. */
  UNAVAILABLE = 'unavailable',
  PRICED = 'priced',
  /** User has selected this quote and bind is being requested from the carrier. */
  REQUEST_TO_BIND = 'request_to_bind',
  /** Coverage is active. Not yet a distinct card treatment — falls back to the minimal card for now. */
  BOUND = 'bound',
}

/** Stages with no coverage/pricing detail to show — just carrier identity + a status line. */
export type MinimalQuoteCardStage =
  | QuoteCardStage.PREPARING
  | QuoteCardStage.APPLICATION_SENT
  | QuoteCardStage.DECLINED
  | QuoteCardStage.ERROR
  | QuoteCardStage.UNAVAILABLE
  | QuoteCardStage.REQUEST_TO_BIND
  | QuoteCardStage.BOUND

/** A line the carrier answered on without a price. */
export interface CoverageOutcome {
  type: CoverageType
  /** Never PRICED — a priced line is a {@link CoverageQuote} in `coverages` instead. */
  stage: MinimalQuoteCardStage
  /** The indication that answered, so a staff lookup can still reach the row behind it. */
  indicationId: string
}

/**
 * Every minimal card holds this height, so a column of them doesn't step up and
 * down with each status line's wrap, and its skeleton can crossfade without the
 * card resizing. 112px clears the tallest state — the two-line error copy — while
 * leaving the header pinned at the priced card's 16px offset.
 */
export const MINIMAL_CARD_MIN_HEIGHT = 'min-h-28'

/**
 * Every card's header holds this height, so the price and coverage rows below it line up
 * across a row of cards whether or not a carrier adds a program, a wholesaler attribution
 * or the customer-uploaded badge under its name.
 */
export const CARD_HEADER_MIN_HEIGHT = 'min-h-14'

/** The "Carrier estimate" tag's explanation, wherever a price is the carrier's estimate. */
export const CARRIER_ESTIMATE_EXPLANATION =
  "This is the carrier's estimate based on the information provided so far. Underwriters confirm the final price before your policy starts."

/** Terminal, unsuccessful outcomes — the carrier icon renders in greyscale for these. */
export const NEGATIVE_OUTCOME_STAGES: ReadonlySet<QuoteCardStage> = new Set([
  QuoteCardStage.DECLINED,
  QuoteCardStage.ERROR,
  QuoteCardStage.UNAVAILABLE,
])

export interface CoverageQuote {
  type: CoverageType
  /** null when the carrier prices the policy as one total (Corgi v2) — a $0 reads as free cover. */
  premium: number | null
  /** Printed in place of the premium. Only our estimate sets it: a Corgi prequote's auto liability. */
  premiumRange?: PremiumRange | null
  /** null when the carrier never stated it — the row prints "—" rather than a $0 we made up. */
  limit: number | null
  deductible: number | null
  /** Physical damage written comprehensive + collision states both; null when one covers both. */
  comprehensiveDeductible?: number | null
  /** Written per occurrence plus a policy aggregate (`limit` is then per occurrence); null when one limit covers both. Not mapped yet. */
  aggregateLimit?: number | null
  /** Physical damage only: the limit totals this many units' values. */
  physicalDamageUnitCount?: number
  /** e.g. cargo coverage that only applies to certain trucks. */
  restrictionNote?: string | null
  /**
   * The indication this coverage was priced on. A grouped card holds coverages from
   * several indications, so the cart key is built from this rather than the card's
   * id — that's what makes checkout apply to every line behind the card.
   */
  indicationId?: string
}

/** Taxes, surcharges and flat charges the carrier adds on top of the coverage premiums. */
export interface OneTimeFee {
  /** Corgi labels one fee per coverage and two coverage types share a label, so rows collide on label alone. */
  id: string
  label: string
  amount: number
  /** The indication charged it. A grouped card holds several, each with its own fees. */
  indicationId?: string
}

/** One way to pay for the policy, e.g. GEICO's "9 Payments". */
export interface PaymentPlan {
  title: string
  dueToday: number
  /** The payments after today's; null when paid in full, or when the carrier gives no figures for them. */
  installments: { count: number; amount: number } | null
  /** What follows today's payment when the carrier gives no installment figures, e.g. Rocklake's financing. */
  balance?: string | null
  /** What the policy costs on this plan, installment fees included; null when the carrier doesn't state it. */
  total: number | null
}

export interface FinancingOffer {
  isStandard: boolean
  /** The carrier record's canned financing text, shown when the carrier sent no plans. */
  description?: string
  /** The carrier's own payment plans for a 12-month policy; only GEICO sends them. */
  plans?: PaymentPlan[]
  /** The carrier's note on its plans, e.g. that installments require auto pay. */
  plansNote?: string
}

/**
 * Factual claims-handling capabilities, sourced from carrier-published info — no
 * Cai-authored rating or opinion. "No information" is the fallback where a carrier
 * doesn't publicly disclose that capability.
 */
export interface ClaimsCapabilities {
  /** Curated flags the filters test. Kept separate from the prose below, which is
      what the card prints — a rephrased sentence must not change what a filter matches. */
  hasMultipleWaysToFile: boolean
  has247Support: boolean
  hasPayoutInformation: boolean
  waysToFile: string
  supportAvailability: string
  payoutSpeed: string
  waysToGetPaid: string
}

export interface Moratorium {
  message: string
}

export interface QuoteDocument {
  url: string
  /** ISO datetime it was last added/updated — shown beside the link, since carriers revise
   * without versioning and it's the only way to judge whether this is the current one. */
  addedAt?: string | null
}

export interface BrokerNote {
  note: string
}

interface QuoteCardBase {
  id: string
  carrierName: string
  iconUrl: string
  iconAlt: string
  /** Who we sourced this offer through. */
  vendor?: Vendor
  /**
   * Token the backend stamps on every row one dispatch wrote, so a carrier's separately-rated
   * lines can be told from the same carrier re-priced later. Absent on hand-added offers.
   */
  offerGroup?: string | null
  /**
   * Every indication this card speaks for — one entry unless {@link groupQuoteCards} merged
   * a carrier's separately-submitted lines. Checkout and staff lookups apply to all of them.
   */
  memberIds?: string[]
  /**
   * The one line this indication rated, for carriers that rate each line on its own
   * submission. A row that never priced holds no coverages, so this is the only thing
   * that names it.
   */
  coverageLine?: CoverageType | null
  /**
   * Lines answered without a price, never overlapping {@link PricedQuoteCard.coverages}.
   * Only ever set on a card that priced something: a breakdown explains a split answer,
   * and with nothing priced there's no split — the card's own status is the whole answer.
   */
  coverageOutcomes?: CoverageOutcome[]
  /** Assigned InsuranceCarrier record; null falls back to vendor branding. */
  carrierId?: string | null
  /**
   * One name for every entity this carrier writes on — "Nirvana (Admitted)",
   * "Nirvana (Non-Admitted)" and the vendor-only row rendered "Nirvana Insurance" are all
   * Nirvana. Null where the row names no carrier and its vendor writes none either: an
   * unassigned wholesaler row wears its wholesaler's label in place of a carrier nobody
   * has named, and two of those are two unknowns rather than one brand.
   */
  carrierBrand?: string | null
  /** Omitted when we have no sourced rating for the carrier — the card hides the row rather than guessing. */
  carrierRating?: CarrierRating
  /** Omitted when we have no sourced COI speed for the carrier. */
  coiSpeed?: CoiSpeed
  /** The carrier's own curated note, shown in Carrier details — independent of brokerNote. */
  carrierPublicNote?: string
  /** Omitted when we don't know whether this carrier is admitted for the customer's state. */
  insuranceType?: InsuranceType | null
  /**
   * The paper behind this offer when the brand quotes on several at once — Rocklake
   * routinely returns three priced Chubb entities and four Palomar ones on one
   * application. Without it those cards are indistinguishable.
   */
  carrierProgram?: string | null
  /**
   * Wholesaler this offer came through, shown under the carrier name. Set on a Rocklake
   * card only when the same brand also came direct from Progressive, so the customer can
   * tell the two cards apart. Rocklake's other carriers carry no attribution.
   */
  sourcedThrough?: string | null
  /**
   * Documents the carrier is holding this offer for. On {@link QuoteCardBase} rather
   * than the priced card because that is exactly where it lands: a carrier waiting on
   * a document has no price yet, so the card is minimal. Each one raises its own
   * prompt — Corgi can be short of a loss run, an IFTA filing, or both at once, and
   * they are satisfied by different files from different places.
   */
  outstandingDocuments?: DocumentType[]
  /** The carrier turned down the loss runs on file as too old, so the loss-run ask wants a recent copy. */
  lossRunStale?: boolean
  /**
   * Why the carrier didn't price, in the words the failure analysis wrote for the customer.
   * Replaces the failed stage's generic copy; set only on a card showing a failure, once analysed.
   */
  failureReason?: string | null
  /** The carrier says this quote expired. See {@link isQuoteExpired} for the full rule. */
  carrierExpired?: boolean
  /** ISO datetime the carrier's own quote expires (Corgi). Replaces the 30-day guess. */
  expiresAt?: string | null
}

/** No coverage/pricing detail yet (or ever, for a terminal non-priced outcome) — just identity + a status line. */
export interface MinimalQuoteCard extends QuoteCardBase {
  stage: MinimalQuoteCardStage
  /**
   * Unaccepted carrier terms, on a card with no price to show yet. Without it a
   * carrier that had stopped and asked for the customer's consent read as
   * "Application sent, waiting for response" — a wait only the customer could end.
   */
  needsAttestation?: boolean
  /** Overrides the generic per-stage message, e.g. a carrier-specific response-time estimate. */
  statusMessage?: string
}

export interface PricedQuoteCard extends QuoteCardBase {
  stage: QuoteCardStage.PRICED
  isCustomerUploaded?: boolean
  /** ISO datetime; set only when isCustomerUploaded is true. */
  uploadedAt?: string
  /** This carrier is the customer's existing insurer. */
  isCurrentInsurer?: boolean
  /** ISO datetime the customer chose this quote at checkout — the indication's `date_applied`. */
  appliedAt?: string | null
  /**
   * When set, a later re-quote superseded this round. It stays on the page and stays
   * checkoutable, so the card has to say so — two prices from one carrier are otherwise
   * indistinguishable, and the older one was rated against older fleet data.
   */
  supersededAt?: string | null
  /** Set by a broker in Console based on conversations with the customer. */
  brokerRecommended?: boolean
  /** Who set that flag — the badge names them, falling back to the assigned broker. */
  recommendedByName?: string
  /** Assigned broker's name, for the recommendation badge and note heading. */
  brokerName?: string
  /** A broker's note on why they're steering the customer toward this quote — rare, not every card has one. */
  brokerNote?: BrokerNote | null
  pricingKind: PricingKind
  /**
   * The price is a carrier estimate priced without the documents or accepted terms
   * the real submission needs, so it can never be checked out. Distinct from having
   * something outstanding: the customer can satisfy every ask and the carrier still
   * not have priced it.
   */
  isEstimateOnly?: boolean
  /**
   * Set when an estimate is standing in front of a failed submission. Shown beside
   * the price, not instead of it: the estimate is a real number the carrier gave for
   * this DOT, and suppressing either half left the quotes page and the Insurance page
   * telling two different stories about the same carrier.
   */
  failedStage?: MinimalQuoteCardStage | null
  /** Set only when pricingKind === INDICATION. */
  indicationStatus?: IndicationStatus | null
  /** ISO date; set only when pricingKind === QUOTED. Expiration is 30 days out. */
  quotedAt?: string | null
  coverages: CoverageQuote[]
  /**
   * Charged on top of the coverage premiums — part of what the year costs, so they
   * count towards the headline price (see {@link getTotalPremium}). Most carriers
   * send none.
   */
  oneTimeFees?: OneTimeFee[]
  /**
   * When set, each coverage can be selected independently (see {@link ItemizedCheckoutStyle}
   * for the two presentation designs). When unset (the default), the coverages form a bundle —
   * a single "Add to cart" applies to all of them, and the customer must accept every coverage
   * listed to get this price.
   */
  itemizedCheckoutStyle?: ItemizedCheckoutStyle
  /** Canonical all-in total (coverages + one-time fees) — the card's headline figure.
   * Falls back to {@link getTotalPremium} over `coverages` when omitted (e.g. design-system fixtures). */
  totalPremium?: number
  /**
   * A merged card's total for each offer behind it, keyed by indication id. Per-line premiums can be
   * null where a carrier prices one sum, so a part-selection prices off these instead.
   */
  offerTotals?: Record<string, number>
  /** The headline as a range, printed in place of totalPremium. Set only on our estimate. */
  totalPremiumRange?: PremiumRange | null
  /** Omitted when we have no sourced financing terms for the carrier. */
  financing?: FinancingOffer
  /** Omitted when we have no sourced claims data for the carrier. */
  claimsCapabilities?: ClaimsCapabilities
  moratorium?: Moratorium | null
  /**
   * The carrier's quote PDFs (parsed or uploaded). A list, not one: Rocklake emails each
   * line separately, so a carrier's merged lines can each carry their own document.
   */
  quoteDocuments?: QuoteDocument[]
}

export type QuoteCardData = MinimalQuoteCard | PricedQuoteCard

/**
 * What the customer pays for the year. Prefers the carrier's own all-in figure and
 * derives one only for fixtures that carry no `totalPremium`. Takes the card rather
 * than its coverages so a caller can't quote a price that leaves the fees out.
 */
export function getTotalPremium(quote: PricedQuoteCard): number {
  // Truthy, not `!== undefined`: the mapper coerces a missing premium to 0, never undefined.
  if (quote.totalPremium) return quote.totalPremium
  const coverages = quote.coverages.reduce((sum, c) => sum + (c.premium ?? 0), 0)
  const fees = (quote.oneTimeFees ?? []).reduce((sum, f) => sum + f.amount, 0)
  return coverages + fees
}

/** The carrier priced the card as one total (Corgi v2): no line has its own price, so "Included" on each says nothing. */
export function isPricedAsOneTotal(coverages: CoverageQuote[]): boolean {
  return coverages.every((coverage) => coverage.premium === null)
}

/** When the quote expires: the carrier's own date where it gave one, else 30 days after quoting. */
export function getExpiration(
  quote: Pick<PricedQuoteCard, 'quotedAt' | 'expiresAt'>
): { date: Date; daysRemaining: number } | null {
  let date: Date
  if (quote.expiresAt) {
    date = new Date(quote.expiresAt)
  } else if (quote.quotedAt) {
    date = new Date(quote.quotedAt)
    date.setDate(date.getDate() + QUOTE_VALIDITY_DAYS)
  } else {
    return null
  }
  const daysRemaining = Math.ceil((date.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
  return { date, daysRemaining }
}

/** The carrier says the quote expired, or its own expiry date has passed. Never true on a 30-day guess. */
export function isQuoteExpired(quote: Pick<QuoteCardData, 'carrierExpired' | 'expiresAt'>): boolean {
  if (quote.carrierExpired) return true
  return !!quote.expiresAt && new Date(quote.expiresAt).getTime() <= Date.now()
}

export type PromptDocumentType = DocumentType.LOSS_RUN | DocumentType.IFTA | DocumentType.MVR

/** One prompt per document, in the order a customer is most likely to have it to hand. */
export const DOCUMENT_PROMPTS: ReadonlyArray<{ documentType: PromptDocumentType; status: IndicationStatus }> =
  [
    { documentType: DocumentType.LOSS_RUN, status: IndicationStatus.AWAITING_LOSS_RUNS },
    { documentType: DocumentType.IFTA, status: IndicationStatus.AWAITING_IFTA },
    { documentType: DocumentType.MVR, status: IndicationStatus.AWAITING_MVR },
  ]

/** The prompts this card still owes, with a stale loss run asking for a recent copy. */
export function outstandingPromptsFor(quote: QuoteCardData): typeof DOCUMENT_PROMPTS {
  return DOCUMENT_PROMPTS.filter((prompt) =>
    (quote.outstandingDocuments ?? []).includes(prompt.documentType)
  ).map((prompt) =>
    prompt.documentType === DocumentType.LOSS_RUN && quote.lossRunStale
      ? { ...prompt, status: IndicationStatus.AWAITING_RECENT_LOSS_RUNS }
      : prompt
  )
}

/**
 * Whether the card's Add to cart is blocked: expired, held back, waiting on terms or documents, or only
 * our estimate. Takes the status separately because QuoteCard reads it as the customer last left it.
 */
export function isCartBlocked(
  quote: PricedQuoteCard,
  indicationStatus: IndicationStatus | null | undefined
): boolean {
  return (
    isQuoteExpired(quote) ||
    !!quote.moratorium ||
    indicationStatus === IndicationStatus.NEEDS_ATTESTATION ||
    outstandingPromptsFor(quote).length > 0 ||
    !!quote.isEstimateOnly
  )
}

/**
 * Maps the real backend indication status to a QuoteCardStage. `PENDING` alone can't
 * distinguish "preparing" from "application sent", so callers pass the indication's
 * `dateSubmitted` — stamped when the application actually went out to the market.
 */
export function stageFromIndicationStatus(
  status: InsuranceIndicationStatus,
  hasBeenSubmitted: boolean
): QuoteCardStage {
  switch (status) {
    case InsuranceIndicationStatus.PENDING:
      return hasBeenSubmitted ? QuoteCardStage.APPLICATION_SENT : QuoteCardStage.PREPARING
    case InsuranceIndicationStatus.GENERATED:
      return QuoteCardStage.PRICED
    case InsuranceIndicationStatus.DECLINED:
      return QuoteCardStage.DECLINED
    case InsuranceIndicationStatus.ERROR:
      return QuoteCardStage.ERROR
    case InsuranceIndicationStatus.UNAVAILABLE:
      return QuoteCardStage.UNAVAILABLE
    // Only ever served with a price on it — the payload withholds a round canceled
    // before it priced, since there'd be nothing on the card to read.
    case InsuranceIndicationStatus.CANCELED:
      return QuoteCardStage.PRICED
  }
}

/** Maps the real backend quote status to a QuoteCardStage. Same PENDING caveat as {@link stageFromIndicationStatus}. */
export function stageFromQuoteStatus(
  status: InsuranceQuoteStatus,
  hasBeenSubmitted: boolean
): QuoteCardStage {
  switch (status) {
    case InsuranceQuoteStatus.PENDING:
      return hasBeenSubmitted ? QuoteCardStage.APPLICATION_SENT : QuoteCardStage.PREPARING
    case InsuranceQuoteStatus.QUOTED:
      return QuoteCardStage.PRICED
    case InsuranceQuoteStatus.REQUEST_TO_BIND:
      return QuoteCardStage.REQUEST_TO_BIND
    case InsuranceQuoteStatus.BOUND:
      return QuoteCardStage.BOUND
    case InsuranceQuoteStatus.DECLINED:
      return QuoteCardStage.DECLINED
    case InsuranceQuoteStatus.ERROR:
      return QuoteCardStage.ERROR
  }
}
